import { CommonModule } from '@angular/common';
import {
  AfterViewInit,
  Component,
  HostBinding,
  inject,
  Input,
  OnChanges,
  OnInit,
} from '@angular/core';
import { Router, RouterModule } from '@angular/router';

import {
  CmsUrl,
  CtaButton,
  CtaPosition,
  CtaSize,
  CtaStyle,
  CtaWidth,
  MarginStyle,
  VisibilitySettingStyle,
} from '@digital-platform/rs-cms/domain';
import { ViewportService } from '@digital-platform/rs-shared/services';
import { IconComponent } from '@digital-platform/rs-shared/ui-brand';
import { BasePTypeComponent } from '../base-ptype/base-ptype.component';
import { NavigationHelperService } from '../utility/navigation-helper.service';

interface CtaButtonAngularConfig {
  containerClass?: string;
  mobileContainerClass?: string;
  midsizeContainerClass?: string;
  desktopContainerClass?: string;
  linkClass?: string;
  mobileLinkClass?: string;
  midsizeLinkClass?: string;
  desktopLinkClass?: string;
}

@Component({
  selector: 'digital-platform-cta-button',
  standalone: true,
  imports: [CommonModule, IconComponent, RouterModule],
  templateUrl: './cta-button.component.html',
  styleUrls: ['./cta-button.component.scss'],
})
export class CtaButtonComponent
  extends BasePTypeComponent
  implements CtaButton, AfterViewInit, OnChanges, OnInit
{
  @HostBinding('class') className = '';
  public viewportService = inject(ViewportService);
  public router = inject(Router);
  public navigationHelperService = inject(NavigationHelperService);
  public override type = 'ctaButton' as const;

  public emptyUrlObj = {
    uri: '',
    title: '',
    options: {
      visibilityClass: {},
      attributes: {
        target: 0,
        class: '',
      },
    },
  };

  @Input() analyticsLinkText?: string | null = '';
  @Input() isButtonDisabled?: boolean = false;
  @Input() chevron = false;
  @Input() ctaPosition?: CtaPosition | null = {};
  @Input() ctaSize?: CtaSize | null = {};
  @Input() ctaStyle?: CtaStyle | null = {};
  @Input() ctaWidth?: CtaWidth | null = {};
  @Input() fontAwesomeIconClass?: string | null = null;
  @Input() marginBottomClass: MarginStyle | null = {};
  @Input() marginTopClass: MarginStyle | null = {};
  @Input() url: CmsUrl | null = [];
  @Input() visibility: VisibilitySettingStyle | null = {};
  @Input() angularConfig?: CtaButtonAngularConfig;
  @Input() linkClass?: string;

  @Input() btnType?: 'primary' | 'secondary' | 'tertiary' = undefined;
  @Input() dark?: boolean = false;
  @Input() size?: 'sm' | 'lg' = undefined;
  @Input() hrefLink?: string = '';

  public linkClasses = '';
  public href = '';
  public cmsUrlData: CmsUrl = [this.emptyUrlObj];
  public count = 0;

  ngOnChanges(): void {
    this.updateCmsUrlData();

    const url = this.cmsUrlData[0]?.uri;

    if (url === 'route:<nolink>') {
      this.href = '';
    } else {
      this.href = url;
    }
  }

  ngOnInit() {
    this.viewportService.whatViewport$.subscribe(() => {
      this.initClassesAndStyles();
    });
  }

  ngAfterViewInit() {
    this.initClassesAndStyles();
  }

  private initClassesAndStyles() {
    const { link, container } = this.concatClasses();
    this.className = container;
    this.linkClasses = link;
  }

  public get buttonClasses(): (string | undefined)[] {
    let extraClass = '';
    if (this.btnType) {
      extraClass = this.buttonRawClass;
      let prefix = 'btn-';
      if (this.hrefLink) prefix = 'button__link_';
      extraClass = extraClass.concat(
        ` ${prefix}${this.btnType}${this.dark ? ' inverted' : ''}`
      );
    }

    return [
      'button',
      this.isButtonDisabled ? 'disabled' : '',
      this.ctaPosition ? this.ctaPosition.alignmentClass : '',
      this.ctaSize ? this.ctaSize.ctaSize : '',
      this.ctaStyle ? this.ctaStyle.ctaStyle : '',
      this.ctaWidth ? this.ctaWidth.ctaWidth : '',
      this.marginBottomClass ? this.marginBottomClass.marginClass : '',
      this.marginTopClass ? this.marginTopClass.marginClass : '',
      this.visibility ? this.visibility.visibilityClass : '',
      this.cmsUrlData[0]?.options?.attributes?.class || '',
      extraClass,
    ].filter((c) => !!c);
  }

  private get buttonRawClass() {
    return this.size === 'sm'
      ? 'button__sm'
      : this.size === 'lg'
      ? 'button__lg'
      : '';
  }

  public navigate(event: Event, url: string): void {
    if (this.isButtonDisabled) {
      event.preventDefault();
      return;
    }

    this.navigationHelperService.navigate(
      event,
      url,
      !!this.cmsUrlData[0].options.attributes.target
    );
  }

  updateCmsUrlData(): void {
    let cmsUrl: CmsUrl = [this.emptyUrlObj];
    if (this.url && this.url.length > 0) {
      cmsUrl = this.url;
    }

    this.cmsUrlData = cmsUrl;
  }

  private concatClasses(): { link: string; container: string } {
    const link = [];
    const container = [];
    if (this.linkClass) {
      link.push(this.linkClass);
    }
    if (this.angularConfig) {
      const { isMobile, isMidsize, isDesktop } = this.viewportService;
      const {
        linkClass,
        midsizeLinkClass,
        mobileLinkClass,
        desktopLinkClass,
        containerClass,
        desktopContainerClass,
        midsizeContainerClass,
        mobileContainerClass,
      } = this.angularConfig;
      link.push(
        linkClass,
        isMobile && mobileLinkClass,
        isMidsize && midsizeLinkClass,
        isDesktop && desktopLinkClass
      );
      container.push(
        containerClass,
        isMobile && mobileContainerClass,
        isMidsize && midsizeContainerClass,
        isDesktop && desktopContainerClass
      );
    }
    return {
      link: link.filter((c) => c).join(' '),
      container: container.filter((c) => c).join(' '),
    };
  }
}
